import { useEffect, useRef, useState } from 'react';
import LoadingOverlay from './components/LoadingOverlay';

// How much wheel/touch delta is needed to go from 0 -> 1
const SCROLL_DISTANCE = 1400;

export default function Home() {
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);

  const setP = (v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    progressRef.current = clamped;
    setProgress(clamped);
  };

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onWheel = (e: WheelEvent) => {
      setP(progressRef.current + e.deltaY / SCROLL_DISTANCE);
    };

    let touchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY ?? 0;
      const dy = touchY - y;
      touchY = y;
      setP(progressRef.current + dy / (SCROLL_DISTANCE * 0.4));
    };

    const onKey = (e: KeyboardEvent) => {
      if (['ArrowDown', 'PageDown', ' '].includes(e.key)) {
        e.preventDefault();
        setP(progressRef.current + 0.08);
      } else if (['ArrowUp', 'PageUp'].includes(e.key)) {
        e.preventDefault();
        setP(progressRef.current - 0.08);
      }
    };

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const contentReveal = Math.max(0, Math.min(1, (progress - 0.75) / 0.25));

  return (
    <div className="relative min-h-screen w-full overflow-hidden text-[#0a0a1a]" style={{ background: '#ffffff' }}>
      {/* Background: airy white canvas with distinct purple vertical light streaks (top-right) and a soft purple glow (bottom-left) */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: '#ffffff' }} />
      {/* Vertical striped purple light beams on the top-right */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, rgba(124,58,237,0) 0px, rgba(124,58,237,0.38) 2px, rgba(124,58,237,0) 26px, rgba(168,85,247,0.30) 40px, rgba(124,58,237,0) 60px)',
          WebkitMaskImage:
            'radial-gradient(ellipse 70% 90% at 90% 0%, black 0%, rgba(0,0,0,0.85) 25%, rgba(0,0,0,0.4) 55%, transparent 75%)',
          maskImage:
            'radial-gradient(ellipse 70% 90% at 90% 0%, black 0%, rgba(0,0,0,0.85) 25%, rgba(0,0,0,0.4) 55%, transparent 75%)',
        }}
      />
      {/* Distinct top-right purple glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 55% 75% at 88% 5%, rgba(124,58,237,0.55) 0%, rgba(168,85,247,0.32) 30%, transparent 62%)',
        }}
      />
      {/* Soft bottom-left purple glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 45% 55% at 10% 90%, rgba(192,132,252,0.45) 0%, rgba(168,85,247,0.20) 35%, transparent 68%)',
        }}
      />

      {/* Star overlay */}
      <LoadingOverlay progress={progress} />

      {/* Foreground Devata hero content */}
      <div
        className="relative z-10 min-h-screen flex flex-col"
        style={{
          opacity: contentReveal,
          transform: `translateY(${(1 - contentReveal) * 24}px)`,
          transition: 'opacity 120ms linear, transform 120ms linear',
          pointerEvents: contentReveal > 0.5 ? 'auto' : 'none',
        }}
      >
        {/* Top navigation */}
        <nav className="w-full flex items-center justify-between px-8 md:px-14 py-6">
          <a href="/" className="flex items-center gap-2.5 cursor-pointer">
            <span
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1a1330] text-white text-[15px] font-semibold"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              D
            </span>
            <span
              className="text-[15px] tracking-[0.28em] font-medium text-[#1a1330]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              DEVATA
            </span>
          </a>

          <ul className="hidden md:flex items-center gap-9 text-[13px] font-medium text-[#1a1330]/80">
            <li>
              <a href="#solutions" className="cursor-pointer hover:text-[#1a1330] transition whitespace-nowrap">
                Solutions
              </a>
            </li>
            <li>
              <a href="#features" className="cursor-pointer hover:text-[#1a1330] transition whitespace-nowrap">
                Features
              </a>
            </li>
            <li>
              <a href="#testimonials" className="cursor-pointer hover:text-[#1a1330] transition whitespace-nowrap">
                Testimonials
              </a>
            </li>
            <li>
              <a href="#pricing" className="cursor-pointer hover:text-[#1a1330] transition whitespace-nowrap">
                Pricing
              </a>
            </li>
            <li>
              <a href="#faq" className="cursor-pointer hover:text-[#1a1330] transition whitespace-nowrap">
                FAQ
              </a>
            </li>
          </ul>

          <a
            href="#sign-in"
            className="inline-flex items-center gap-2 rounded-full border border-[#1a1330]/15 bg-white/70 backdrop-blur px-5 py-2.5 text-[13px] font-medium text-[#1a1330] cursor-pointer whitespace-nowrap hover:bg-white hover:border-[#1a1330]/30 transition-colors"
          >
            <i className="ri-user-line text-[14px]"></i>
            Sign In
          </a>
        </nav>

        {/* Hero */}
        <main className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-24">
          <h1
            className="font-normal text-[#0a0a1a] leading-[1.02] tracking-tight text-5xl md:text-6xl lg:text-[92px] max-w-5xl"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Automate routine
            <br />
            tasks with Devata
          </h1>

          <p className="mt-7 max-w-md text-[15px] leading-relaxed text-[#1a1330]/70">
            Devata helps data scientists to get the most out of AI with its easy-to-use platform
          </p>

          <a
            href="#get-started"
            className="mt-10 inline-flex items-center rounded-full bg-[#1a1330] px-9 py-4 text-[14px] font-medium text-white cursor-pointer whitespace-nowrap hover:bg-[#2a2148] transition-colors"
          >
            Get Started
          </a>
        </main>
      </div>
    </div>
  );
}